import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  PRIMARY_LIGHT,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  RADIUS_MD,
  SHADOW_LG,
  STROKE_GREY,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaPaneSelect — pane-head dropdown matching the mockup's .l4-select.
 *
 * Props:
 *   caption     string                              — small uppercase label
 *   options     Array<{ value, label, crumb? }>     — selectable rows
 *   value       any                                 — currently selected value
 *   onChange    (value) => void
 *   searchable  boolean                             — show a search box
 *   menuWidth   number                              — dropdown menu width (px)
 *   minWidth    number                              — control min width (px)
 */
function BetaPaneSelect({
  caption,
  options,
  value,
  onChange,
  searchable,
  menuWidth,
  minWidth,
  compact,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedLabel = useMemo(
    () => options.find(o => o.value === value)?.label || options[0]?.label || '',
    [options, value],
  );

  const lc = search.trim().toLowerCase();
  const filtered = useMemo(
    () => (lc ? options.filter(o => o.label.toLowerCase().includes(lc)) : options),
    [options, lc],
  );

  const select = v => {
    onChange(v);
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      ref={rootRef}
      className={css(styles.select, compact && styles.selectCompact, open && styles.selectOpen)}
      style={{ minWidth }}
      onClick={() => setOpen(o => !o)}
    >
      <div className={css(styles.label, compact && styles.labelCompact)}>
        <span className={css(styles.cap, compact && styles.capCompact)}>{caption}</span>
        <span className={css(styles.val, compact && styles.valCompact)}>{selectedLabel}</span>
      </div>
      <FontAwesomeIcon
        icon={faChevronDown}
        className={css(styles.chevron, open && styles.chevronOpen)}
      />

      {open && (
        <div
          className={css(styles.menu)}
          style={{ width: menuWidth }}
          onClick={e => e.stopPropagation()}
        >
          {searchable && (
            <div className={css(styles.menuSearch)}>
              <FontAwesomeIcon icon={faSearch} className={css(styles.searchIcon)} />
              <input
                className={css(styles.searchInput)}
                placeholder="Find category…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <div className={css(styles.menuList)}>
            {filtered.map(o => (
              <div
                key={String(o.value)}
                className={css(styles.opt, o.value === value && styles.optActive)}
                onClick={() => select(o.value)}
              >
                <span>{o.label}</span>
                {o.crumb && <span className={css(styles.crumb)}>{o.crumb}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  select: {
    position: 'relative',
    cursor: 'pointer',
    backgroundColor: WHITE,
    border: `1px solid ${STROKE_GREY}`,
    borderRadius: RADIUS_MD,
    padding: '8px 12px 8px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    ':hover': {
      backgroundColor: BG_2,
    },
  },

  // Compact horizontal layout — caption and value side by side, less padding.
  // Used in the Products page controls row to keep the dropdowns inline with
  // the items count without doubling the row height.
  selectCompact: {
    padding: '6px 10px 6px 12px',
    gap: 8,
  },

  selectOpen: {
    borderColor: PRIMARY_COLOR,
    boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA}`,
    backgroundColor: WHITE,
  },

  label: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },

  labelCompact: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
  },

  cap: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: 10,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  capCompact: {
    flexShrink: 0,
  },

  val: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  valCompact: {
    marginTop: 0,
  },

  chevron: {
    fontSize: 12,
    color: FG_3,
    opacity: 0.55,
    flexShrink: 0,
    transition: 'transform 0.15s',
  },

  chevronOpen: {
    transform: 'rotate(180deg)',
  },

  menu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    zIndex: 30,
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_LG,
    overflow: 'hidden',
    cursor: 'default',
  },

  menuSearch: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderBottom: `1px solid ${BORDER_2}`,
    backgroundColor: BG_2,
  },

  searchIcon: {
    fontSize: 14,
    color: FG_3,
    opacity: 0.55,
    flexShrink: 0,
  },

  searchInput: {
    border: 'none',
    background: 'transparent',
    outline: 'none',
    flex: 1,
    fontFamily: FONT_BODY,
    fontWeight: 400,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    minWidth: 0,
  },

  menuList: {
    maxHeight: 360,
    overflowY: 'auto',
    padding: '4px 0',
  },

  opt: {
    padding: '8px 14px',
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    ':hover': {
      backgroundColor: BG_2,
    },
  },

  optActive: {
    backgroundColor: PRIMARY_LIGHT,
    color: PRIMARY_HOVER,
    fontWeight: FONT_WEIGHT_BOLD,
  },

  crumb: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: 11,
    color: FG_3,
  },
});

BetaPaneSelect.propTypes = {
  caption: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string.isRequired,
      crumb: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  searchable: PropTypes.bool,
  menuWidth: PropTypes.number,
  minWidth: PropTypes.number,
  compact: PropTypes.bool,
};

BetaPaneSelect.defaultProps = {
  value: null,
  searchable: false,
  menuWidth: 280,
  minWidth: 200,
  compact: false,
};

export default BetaPaneSelect;
